// Password Reset Service
// This is a mock service that simulates password reset functionality
// In a real application, this would integrate with your backend API

export interface PasswordResetRequest {
    email: string;
}

export interface PasswordResetResponse {
    success: boolean;
    message: string;
    token?: string;
}

export interface ResetPasswordRequest {
    token: string;
    newPassword: string;
}

// Mock database to store reset tokens
const resetTokens = new Map<string, { email: string; expires: Date }>();

// Mock email service
const mockEmailService = {
    async sendResetEmail(email: string, token: string): Promise<boolean> {
        console.log(`📧 Mock Email Sent to: ${email}`);
        console.log(`🔗 Reset Token: ${token}`);
        console.log(`🔗 Reset Link: ${window.location.origin}/reset-password?token=${token}`);
        
        // Simulate email sending delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return true; // Always succeed in mock
    }
};

export const passwordResetService = {
    // Send password reset email
    async requestPasswordReset(email: string): Promise<PasswordResetResponse> {
        try {
            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                return {
                    success: false,
                    message: 'Invalid email address'
                };
            }

            // In a real app, you would check if the email exists in your database
            // For now, we'll accept any valid email format
            
            // Generate a reset token
            const token = this.generateResetToken();
            
            // Store the token with expiration (1 hour)
            const expires = new Date();
            expires.setHours(expires.getHours() + 1);
            resetTokens.set(token, { email, expires });
            
            // Send email (mock)
            await mockEmailService.sendResetEmail(email, token);
            
            return {
                success: true,
                message: 'Password reset link has been sent to your email address.',
                token // In production, don't return the token in the response
            };
        } catch (error) {
            console.error('Password reset request failed:', error);
            return {
                success: false,
                message: 'Failed to send password reset email. Please try again.'
            };
        }
    },

    // Reset password with token
    async resetPassword(token: string, newPassword: string): Promise<PasswordResetResponse> {
        try {
            // Validate token
            const tokenData = resetTokens.get(token);
            if (!tokenData) {
                return {
                    success: false,
                    message: 'Invalid or expired reset link.'
                };
            }

            // Check if token has expired
            if (new Date() > tokenData.expires) {
                resetTokens.delete(token);
                return {
                    success: false,
                    message: 'Reset link has expired. Please request a new one.'
                };
            }

            // Validate new password
            if (!newPassword || newPassword.length < 6) {
                return {
                    success: false,
                    message: 'Password must be at least 6 characters long.'
                };
            }

            // In a real app, you would update the password in your database
            // For now, we'll just simulate success
            
            // Clean up the token
            resetTokens.delete(token);
            
            console.log(`🔒 Password reset successful for: ${tokenData.email}`);
            
            return {
                success: true,
                message: 'Password has been reset successfully!'
            };
        } catch (error) {
            console.error('Password reset failed:', error);
            return {
                success: false,
                message: 'Failed to reset password. Please try again.'
            };
        }
    },

    // Validate reset token
    validateToken(token: string): boolean {
        const tokenData = resetTokens.get(token);
        if (!tokenData) return false;
        
        if (new Date() > tokenData.expires) {
            resetTokens.delete(token);
            return false;
        }
        
        return true;
    },

    // Generate a secure reset token
    generateResetToken(): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < 32; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    },

    // Get email for token (for display purposes)
    getEmailForToken(token: string): string | null {
        const tokenData = resetTokens.get(token);
        return tokenData?.email || null;
    }
};

